import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/ui/use-toast';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';

export interface FeeStructure {
  id?: string;
  fee_type: 'percentage' | 'fixed' | 'tiered';
  fee_percentage?: number;
  fee_fixed_cents?: number;
  min_fee_cents?: number;
  max_fee_cents?: number;
  currency: string;
  applies_to: string;
  volume_tiers?: Array<{
    min_volume: number;
    discount_percentage: number;
  }>;
  effective_from?: string;
  effective_to?: string;
  active?: boolean;
}

export interface FeeCalculation {
  amount_cents: number;
  currency: string;
  fee_cents: number;
  fee_percentage: number;
  net_amount_cents: number;
  fee_structure: {
    base_fee_cents: number;
    volume_discount: number;
    monthly_volume_cents: number;
    fee_type: string;
    currency: string;
  };
}

export interface VolumeAnalysis {
  period_days: number;
  analysis: {
    total_payments: number;
    total_volume_cents: number;
    total_fees_cents: number;
    average_payment_cents: number;
    effective_fee_rate: string;
    daily_average_volume_cents: number;
    weekly_trends: Array<{
      week: string;
      volume_cents: number;
      fees_cents: number;
      payment_count: number;
      average_payment_cents: number;
    }>;
  };
  potential_discounts: Array<{
    tier: string;
    discount: number;
    volume_required: number;
    eligible: boolean;
    current_volume: number;
    volume_needed: number;
  }>;
}

export interface PlatformFeesHook {
  feeStructure: FeeStructure | null;
  loading: boolean;
  error: string | null;
  calculateFee: (businessId: string, amountCents: number, currency?: string) => Promise<FeeCalculation | null>;
  loadFeeStructure: (businessId: string) => Promise<void>;
  getVolumeAnalysis: (businessId: string, days?: number) => Promise<VolumeAnalysis | null>;
  previewFeeChanges: (businessId: string, newStructure: FeeStructure, sampleAmounts?: number[]) => Promise<any>;
  hasCustomStructure: boolean;
  defaultStructure: FeeStructure;
}

export const usePlatformFees = (businessId: string): PlatformFeesHook => {
  const [feeStructure, setFeeStructure] = useState<FeeStructure | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const defaultStructure: FeeStructure = {
    fee_type: 'percentage',
    fee_percentage: 0.025, // 2.5%
    min_fee_cents: 50, // 50 cents
    currency: 'EUR',
    applies_to: 'payroll_transfers',
    active: true
  };

  const hasCustomStructure = feeStructure !== null && feeStructure.id !== undefined;

  const loadFeeStructure = useCallback(async (businessId: string) => {
    if (!businessId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const { data, error } = await supabase.functions.invoke('platform-fee-calculator/fee-structure', {
        body: JSON.stringify({ business_id: businessId })
      });

      if (error) {
        throw new Error(error.message || 'Failed to load fee structure');
      }

      if (data.has_custom_structure) {
        setFeeStructure(data.fee_structure);
      } else {
        setFeeStructure(defaultStructure);
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to load fee structure';
      setError(errorMessage);
      console.error('Error loading fee structure:', err);
      // Set default structure on error
      setFeeStructure(defaultStructure);
    } finally {
      setLoading(false);
    }
  }, []);

  const calculateFee = useCallback(async (
    businessId: string, 
    amountCents: number, 
    currency: string = 'EUR'
  ): Promise<FeeCalculation | null> => {
    if (!businessId || !amountCents) return null;
    
    try {
      const { data, error } = await supabase.functions.invoke('platform-fee-calculator/calculate', {
        body: JSON.stringify({
          business_id: businessId,
          amount_cents: amountCents,
          currency,
          calculation_type: 'preview'
        })
      });

      if (error) {
        throw new Error(error.message || 'Failed to calculate fee');
      }

      return {
        amount_cents: amountCents,
        currency,
        fee_cents: data.fee_cents,
        fee_percentage: data.fee_percentage,
        net_amount_cents: data.net_amount_cents,
        fee_structure: data.fee_structure
      };
    } catch (err: any) {
      console.error('Error calculating fee:', err);
      // Return fallback calculation using default structure
      const fallbackFee = Math.max(
        Math.round(amountCents * (defaultStructure.fee_percentage || 0.025)),
        defaultStructure.min_fee_cents || 50
      );
      
      return {
        amount_cents: amountCents,
        currency,
        fee_cents: fallbackFee,
        fee_percentage: fallbackFee / amountCents,
        net_amount_cents: amountCents - fallbackFee,
        fee_structure: {
          base_fee_cents: fallbackFee,
          volume_discount: 0,
          monthly_volume_cents: 0,
          fee_type: 'percentage',
          currency
        }
      };
    }
  }, []);

  const getVolumeAnalysis = useCallback(async (
    businessId: string, 
    days: number = 30
  ): Promise<VolumeAnalysis | null> => {
    if (!businessId) return null;
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('platform-fee-calculator/volume-analysis', {
        body: JSON.stringify({
          business_id: businessId,
          days
        })
      });

      if (error) {
        throw new Error(error.message || 'Failed to get volume analysis');
      }

      return data;
    } catch (err: any) {
      console.error('Error getting volume analysis:', err);
      setError(err.message || 'Failed to get volume analysis');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const previewFeeChanges = useCallback(async (
    businessId: string,
    newStructure: FeeStructure,
    sampleAmounts?: number[]
  ) => {
    if (!businessId || !newStructure) return null;
    
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('platform-fee-calculator/preview-fee-changes', {
        body: JSON.stringify({
          business_id: businessId,
          new_fee_structure: newStructure,
          sample_amounts: sampleAmounts
        })
      });

      if (error) {
        throw new Error(error.message || 'Failed to preview fee changes');
      }

      return data;
    } catch (err: any) {
      console.error('Error previewing fee changes:', err);
      setError(err.message || 'Failed to preview fee changes');
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Load fee structure on mount and when businessId changes
  useEffect(() => {
    if (businessId) {
      loadFeeStructure(businessId);
    }
  }, [businessId, loadFeeStructure]);

  // Set up real-time subscription for fee structure changes
  useEffect(() => {
    if (!businessId) return;

    const subscription = supabase
      .channel(`platform_fees_${businessId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'platform_fees',
          filter: `business_id=eq.${businessId}`,
        },
        (payload) => {
          // Skip refresh if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;

          console.log('Fee structure updated:', payload);
          loadFeeStructure(businessId);
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [businessId, loadFeeStructure]);

  return {
    feeStructure,
    loading,
    error,
    calculateFee,
    loadFeeStructure,
    getVolumeAnalysis,
    previewFeeChanges,
    hasCustomStructure,
    defaultStructure,
  };
};